import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from '../theme/theme';
import { darkTheme } from '../theme/darkTheme';
import { edtechTheme } from '../theme/edtechTheme';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Check localStorage for saved preference
    try {
      const savedMode = localStorage.getItem('themeMode');
      // Migrate 'light' to 'edtech' since we only support edtech and dark now
      if (savedMode === 'light') {
        return 'edtech';
      }
      return savedMode || 'edtech';
    } catch (e) {
      return 'edtech';
    }
  });

  useEffect(() => {
    // Save preference to localStorage
    try {
      localStorage.setItem('themeMode', mode);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      if (prevMode === 'dark') return 'edtech';
      return 'dark';
    });
  };

  const setTheme = (newMode) => {
    setMode(newMode);
  };

  const getCurrentTheme = () => {
    switch (mode) {
      case 'dark':
        return darkTheme;
      case 'edtech':
        return edtechTheme;
      default:
        return theme;
    }
  };

  const currentTheme = getCurrentTheme();

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme }}>
      <MUIThemeProvider theme={currentTheme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
